import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, IconButton, Snackbar, Stack, Tooltip, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import VideocamIcon from '@mui/icons-material/Videocam';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { uploadRecording } from '../../services/gnMediaService';
import type { GnRole } from './GnContext';

const AUDIO_MAX_SECONDS = 600; // 10 min (spec 046 FR-004)
const VIDEO_MAX_SECONDS = 300; // 5 min

interface MediaRecorderControlProps {
  role: GnRole;
  courseId: number;
  nodeId: number | null;
  /** Insert the uploaded clip into the notebook as a mediaClip node. */
  onClipReady: (assetId: number, mediaType: 'AUDIO' | 'VIDEO', durationSeconds: number) => void;
}

/**
 * Record-yourself audio/video for the GN page (spec 046 FR-004, US1). The client timer
 * stops the recording gracefully at the cap — the clip up to the limit is kept and the
 * user is told she can start a new one; the server re-enforces the caps on upload.
 */
const MediaRecorderControl: React.FC<MediaRecorderControlProps> = ({ role, courseId, nodeId, onClipReady }) => {
  const [recording, setRecording] = useState<'AUDIO' | 'VIDEO' | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const capTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hitCapRef = useRef(false);

  const cleanupTimers = () => {
    if (tickerRef.current) clearInterval(tickerRef.current);
    if (capTimerRef.current) clearTimeout(capTimerRef.current);
    tickerRef.current = null;
    capTimerRef.current = null;
  };

  const stopRecording = useCallback(() => {
    recorderRef.current?.stop();
  }, []);

  const startRecording = async (mediaType: 'AUDIO' | 'VIDEO') => {
    if (typeof MediaRecorder === 'undefined') {
      setNotice('Recording is not supported in this browser.');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(
        mediaType === 'AUDIO' ? { audio: true } : { audio: true, video: true }
      );
    } catch {
      setNotice('Recording needs microphone' + (mediaType === 'VIDEO' ? '/camera' : '') + ' permission.');
      return;
    }

    const recorder = new MediaRecorder(stream);
    recorderRef.current = recorder;
    chunksRef.current = [];
    hitCapRef.current = false;
    startedAtRef.current = Date.now();
    setElapsed(0);
    setRecording(mediaType);

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      cleanupTimers();
      stream.getTracks().forEach(t => t.stop());
      const durationSeconds = Math.min(
        Math.round((Date.now() - startedAtRef.current) / 1000),
        mediaType === 'AUDIO' ? AUDIO_MAX_SECONDS : VIDEO_MAX_SECONDS
      );
      setRecording(null);
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'video/webm' });
      if (blob.size === 0 || durationSeconds === 0) {
        setNotice('Nothing was recorded.');
        return;
      }
      setUploading(true);
      try {
        const result = await uploadRecording(role, courseId, nodeId, blob, mediaType, durationSeconds);
        onClipReady(result.assetId, result.mediaType, result.durationSeconds);
        setNotice(hitCapRef.current
          ? 'Recording reached the time limit — the clip was saved. You can start a new one.'
          : 'Recording added to your notebook.');
      } catch (err: any) {
        setNotice(err?.response?.status === 413
          ? (err?.response?.data?.message || 'Recording is over the size limit.')
          : 'Upload failed — the recording was not saved.');
      } finally {
        setUploading(false);
      }
    };

    recorder.start(1000);

    tickerRef.current = setInterval(
      () => setElapsed(Math.round((Date.now() - startedAtRef.current) / 1000)), 1000);
    const capMs = (mediaType === 'AUDIO' ? AUDIO_MAX_SECONDS : VIDEO_MAX_SECONDS) * 1000;
    capTimerRef.current = setTimeout(() => {
      hitCapRef.current = true;
      stopRecording();
    }, capMs);
  };

  useEffect(() => () => {
    cleanupTimers();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  }, []);

  const format = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {recording ? (
        <>
          <Typography variant="caption" color="error" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            ● {format(elapsed)}
          </Typography>
          <Tooltip title="Stop recording">
            <IconButton size="small" color="error" onClick={stopRecording} data-testid="gn-record-stop">
              <StopCircleIcon />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <>
          <Tooltip title="Record an audio note (max 10 min)">
            <span>
              <IconButton size="small" onClick={() => startRecording('AUDIO')} disabled={uploading} data-testid="gn-record-audio">
                <MicIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Record a video note (max 5 min)">
            <span>
              <IconButton size="small" onClick={() => startRecording('VIDEO')} disabled={uploading} data-testid="gn-record-video">
                <VideocamIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
      <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice(null)}>
        <Alert onClose={() => setNotice(null)} severity="info" variant="filled" sx={{ width: '100%' }}>
          {notice}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default MediaRecorderControl;
